import { motion } from 'framer-motion'
import { ArrowRight, Calendar, Mail } from 'lucide-react'
import { TestOpsCommandLogo } from '@ytest/testops-command'

const YTEST_URL = import.meta.env.VITE_YTEST_URL || 'https://ytest.ai'

export function CTA({ onScheduleDemo }: { onScheduleDemo: () => void }) {
  return (
    <section id="contact" className="relative isolate overflow-hidden bg-zinc-950 py-24 sm:py-32">
      {/* Big glow — cyan family. */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 mx-auto h-[32rem] w-[56rem] rounded-full bg-gradient-to-r from-cyan-500/20 via-sky-500/20 to-blue-500/20 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur"
        >
          <TestOpsCommandLogo size={48} />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8 text-5xl font-bold tracking-tight text-white sm:text-6xl"
        >
          Ready to{' '}
          <span className="bg-gradient-to-r from-cyan-400 to-sky-300 bg-clip-text text-transparent">
            command
          </span>{' '}
          your test ops?
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mx-auto mt-6 max-w-2xl text-lg text-white/70"
        >
          Free trial. No credit card. Connect your stack and ask your first question
          in under five minutes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4"
        >
          <a
            href={`${YTEST_URL}/signup`}
            className="group inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-6 py-3.5 text-base font-semibold text-white shadow-2xl shadow-cyan-500/30 transition hover:-translate-y-0.5 hover:bg-cyan-600 hover:shadow-cyan-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
          >
            Start free trial
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <button
            onClick={onScheduleDemo}
            className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-6 py-3.5 text-base font-semibold text-white/90 backdrop-blur transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            <Calendar className="h-4 w-4 text-cyan-400" />
            Schedule a demo
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8"
        >
          <a
            href="mailto:hello@ytest.ai"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
          >
            <Mail className="h-4 w-4 text-cyan-400" />
            hello@ytest.ai
          </a>
        </motion.div>
      </div>
    </section>
  )
}
